// client/src/components/auth/ForgotPassword.js - TRADOTTO
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../layout/LanguageSelector';

// Security utilities compatti
const S = {
  limits: new Map(),
  
  check(email) {
    const k = email.toLowerCase(), now = Date.now();
    const l = this.limits.get(k) || { c: 0, t: now };
    if (now - l.t > 3600000) { this.limits.set(k, { c: 0, t: now }); return true; }
    return l.c < 3; // 3 tentativi/ora
  },
  
  fail(email) {
    const k = email.toLowerCase(), l = this.limits.get(k) || { c: 0, t: Date.now() };
    l.c++; this.limits.set(k, l);
  },
  
  emailOk(email) { 
    return email && email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },
  
  threat(input) { 
    return [/<script[^>]*>/i, /<iframe[^>]*>/i, /javascript\s*:/i, /on\w+\s*=/i].some(p => p.test(input)); 
  },
  
  clean(msg) { 
    if (!msg || typeof msg !== 'string') return '';
    return msg.replace(/<script[^>]*>.*?<\/script>/gi, '').trim().substring(0, 200); 
  }
};

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  
  const lastSubmit = useRef(0);

  const onChange = e => {
    const value = e.target.value;
    
    if (value.length > 254) {
      setWarning(t('auth.forgotPassword.errors.emailTooLong'));
      return;
    }
    
    if (S.threat(value)) {
      setWarning(t('auth.forgotPassword.errors.invalidContent'));
      return;
    }
    
    if (warning) setWarning('');
    if (error) setError('');
    if (message) setMessage('');
    
    setEmail(value.toLowerCase().trim());
  };

  const onSubmit = async e => {
    e.preventDefault();
    setMessage('');
    setError('');
    setWarning('');

    if (!email) {
      return setError(t('auth.forgotPassword.errors.emailRequired'));
    }

    if (!S.emailOk(email)) {
      return setError(t('auth.forgotPassword.errors.validEmail'));
    }

    if (S.threat(email)) {
      return setError(t('auth.forgotPassword.errors.invalidChars'));
    }
    
    if (!S.check(email)) {
      return setError(t('auth.forgotPassword.errors.tooManyRequests'));
    }
    
    const now = Date.now();
    if (now - lastSubmit.current < 3000) {
      return setError(t('auth.forgotPassword.errors.waitBefore'));
    }
    lastSubmit.current = now;

    setLoading(true);

    try {
      S.fail(email);
      
      const res = await axios.post('/api/auth/forgot-password', { 
        email: email.toLowerCase().trim() 
      }, { timeout: 30000 });
      
      setMessage(t('auth.forgotPassword.resetLinkSent')); // Messaggio standard
      setEmail('');
      
    } catch (err) {
      let errorMessage = t('auth.forgotPassword.errors.requestFailed');
      
      if (err.response?.data?.message) {
        const serverMsg = S.clean(err.response.data.message);
        if (serverMsg.length > 0) errorMessage = serverMsg;
      } else if (err.response?.status === 429) {
        errorMessage = t('auth.forgotPassword.errors.tooManyRequestsServer');
      } else if (err.response?.status === 404) {
        errorMessage = t('auth.forgotPassword.errors.emailNotFound');
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = t('auth.forgotPassword.errors.timeout');
      } else if (!err.response) {
        errorMessage = t('auth.forgotPassword.errors.networkError');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="auth-card">
        <h2>{t('auth.forgotPassword.title')}</h2>
        <p className="auth-subtitle">{t('auth.forgotPassword.subtitle')}</p>
        
        {warning && <div className="alert alert-warning">{warning}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}
        
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('auth.forgotPassword.emailLabel')}</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder={t('auth.forgotPassword.emailPlaceholder')}
              value={email}
              onChange={onChange}
              maxLength="254"
              autoComplete="email"
              required
              disabled={loading}
            />
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={loading || !!warning}
          >
            {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendButton')}
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            {t('auth.forgotPassword.rememberPassword')}{' '}
            <Link to="/login">{t('auth.forgotPassword.loginHere')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;